import { IconsProvider } from "../icons/context.tsx";
import { NO_ICONS, type IconSource } from "../icons/page-icons.ts";
import { createRef } from "react";
import { createRoot } from "react-dom/client";
import "../../ui/kit/styles.ts";
import "./chat.css";
import "./chat-bar.css";
import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import { ChatPanel, type ChatInputHooks } from "./panel.tsx";
import type { ChatSizeSetting } from "./sizing.ts";
import type { ChatSource } from "./store.ts";

/**
 * The game page's chat panel (P1.9-T23, ADR-068 decision 8): a React root of its own in the page's `#chat` host,
 * before `#hud` in the overlay so the message line draws over it, following the chat store. It is `inert` until
 * the world is revealed, as the HUD is, and again once the page leaves it (P1.9-T15), so neither the keyboard
 * nor assistive technology reaches it behind the loading shell. The input opens a layer on the page's input router while it has focus, so Escape, which the
 * router answers first, closes the layer and leaves the input with its draft, and never asks for the game menu
 * while the player types; a blur by any other path, a click on the world or Tab, closes the layer too, so no
 * layer outlives the input's focus. The stepper's list holds a second layer over it while it shows (ADR-068's
 * amendment "chat commands, Discord style", item 2), so Escape closes the list first and the input next; the panel
 * lets that layer go whenever the list hides for any other reason, so it never outlives the list. The scope picker's
 * list (ADR-068's amendment "the entry row as redrawn, and the scope picker") holds a layer of its own the same way,
 * so Escape closes it before anything under it.
 */

/**
 * The chat panel as the page drives it.
 */
export interface ChatControl {
  /**
   * Opens the input for typing, as Enter does while the game owns the keyboard.
   *
   * @returns True when the input took focus: the world is shown and the connection speaks chat.
   */
  open(): boolean;
  /** Tells the panel the world is revealed, so it takes input from now on. */
  reveal(): void;
  /**
   * Tells the panel the page left the world (P1.9-T15): it goes inert behind the loading shell's notice, as
   * before the first reveal, and Enter opens nothing until the next reveal.
   */
  hide(): void;
}

/**
 * Mounts the chat panel in its host.
 *
 * @param host The page's `#chat` element in the overlay root, `inert` until the reveal.
 * @param store The chat store the panel reads and sends through.
 * @param input The page's input router, on which the input opens its layer.
 * @param sizing The page's chat size setting, which Settings changes too; the panel keeps one of its own without it.
 * @returns The panel's control.
 * @example
 * const chat = mountChat(document.getElementById("chat") as HTMLElement, chatStore, input);
 * input.onKey((event) => { if (event.key === "Enter" && chat.open()) event.preventDefault(); });
 */
export function mountChat(host: HTMLElement, store: ChatSource, input: Pick<InputRouter, "open">, sizing?: ChatSizeSetting, icons: IconSource = NO_ICONS): ChatControl {
  const inputRef = createRef<HTMLInputElement>();
  let layer: LayerHandle | null = null;
  let listLayer: LayerHandle | null = null;
  let pickerLayer: LayerHandle | null = null;
  let revealed = false;

  /**
   * Closes the input's layer, once.
   */
  const closeLayer = (): void => {
    const open = layer;
    layer = null;
    open?.close();
  };

  /**
   * Lets the list's layer go, once, without dismissing the list.
   */
  const closeListLayer = (): void => {
    const open = listLayer;
    listLayer = null;
    open?.close();
  };

  const hooks: ChatInputHooks = {
    inputRef,
    /**
     * Opens the router's layer as the input takes focus, however it took it.
     */
    onOpen: () => {
      if (layer === null) {
        layer = input.open({ onClose: () => inputRef.current?.blur() });
      }
    },
    /**
     * Closes the layer as the input loses focus, however it lost it.
     */
    onLeave: closeLayer,
    /**
     * Leaves the input after a line is sent: the layer closes, which blurs the input.
     */
    leave: () => {
      if (layer === null) {
        inputRef.current?.blur();
      } else {
        closeLayer();
      }
    },
    /**
     * Gives the input the focus, which opens its layer.
     */
    focus: () => {
      inputRef.current?.focus();
    },
    /**
     * Holds the list's layer while the list shows; Escape closing it dismisses the list, and the list hiding lets
     * it go.
     *
     * @param shown Whether the list shows.
     * @param onDismiss Told when Escape closes the layer.
     */
    list: (shown, onDismiss) => {
      if (!shown) {
        closeListLayer();
      } else if (listLayer === null) {
        const opened = input.open({
          onClose: () => {
            if (listLayer === opened) {
              listLayer = null;
              onDismiss();
            }
          },
        });
        listLayer = opened;
      }
    },
    /**
     * Holds the scope picker's layer while its list shows; Escape closing it closes the list, and the list hiding
     * lets it go.
     *
     * @param shown Whether the picker's list shows.
     * @param onDismiss Told when Escape closes the layer.
     */
    picker: (shown, onDismiss) => {
      if (!shown) {
        const open = pickerLayer;
        pickerLayer = null;
        open?.close();
      } else if (pickerLayer === null) {
        const opened = input.open({
          onClose: () => {
            if (pickerLayer === opened) {
              pickerLayer = null;
              onDismiss();
            }
          },
        });
        pickerLayer = opened;
      }
    },
  };

  createRoot(host).render(
    <IconsProvider source={icons}>
      <ChatPanel store={store} hooks={hooks} sizing={sizing} />
    </IconsProvider>,
  );

  return {
    open: () => {
      const element = inputRef.current;
      const { shown, connected } = store.getSnapshot();
      if (!revealed || !shown || !connected || element === null || element.disabled) {
        return false;
      }
      element.focus();
      return document.activeElement === element;
    },
    reveal: () => {
      revealed = true;
      host.inert = false;
    },
    hide: () => {
      revealed = false;
      host.inert = true;
    },
  };
}
